import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Check, X, Pencil } from 'lucide-react';
import { cn } from '@/lib/utils';

interface GameRulesEditableRowProps {
  label: string;
  value: number;
  description?: string;
  isAdmin: boolean;
  onSave: (newValue: number) => Promise<boolean>;
  prefix?: string;
  suffix?: string;
  labelClassName?: string;
  valueClassName?: string;
  isCurrency?: boolean;
}

const GameRulesEditableRow: React.FC<GameRulesEditableRowProps> = ({
  label,
  value,
  description,
  isAdmin,
  onSave,
  prefix = '+',
  suffix = '',
  labelClassName,
  valueClassName,
  isCurrency = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value.toString());
  const [isSaving, setIsSaving] = useState(false);

  const formatDisplayValue = (val: number) => {
    if (isCurrency) {
      return `₡${(val / 1000).toFixed(0)}k`;
    }
    return `${prefix}${val}${suffix}`;
  };

  const handleSave = async () => {
    const numValue = parseInt(editValue, 10);
    if (isNaN(numValue) || numValue < 0) {
      setEditValue(value.toString());
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    const success = await onSave(numValue);
    setIsSaving(false);

    if (success) {
      setIsEditing(false);
    } else {
      setEditValue(value.toString());
    }
  };

  const handleCancel = () => {
    setEditValue(value.toString());
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <tr className="border-b border-zinc-800">
      <td className={cn("py-2 font-medium", labelClassName)}>{label}</td>
      <td className="py-2 text-zinc-400">{description}</td>
      <td className="text-right">
        {isEditing ? (
          <div className="flex items-center justify-end gap-1">
            <Input
              type="number"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-20 h-7 text-right bg-zinc-800 border-zinc-600 text-white text-xs"
              autoFocus
              disabled={isSaving}
            />
            <Button
              size="icon"
              variant="ghost"
              className="h-6 w-6 text-green-400 hover:text-green-300 hover:bg-green-900/20"
              onClick={handleSave}
              disabled={isSaving}
            >
              <Check className="h-3 w-3" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-6 w-6 text-red-400 hover:text-red-300 hover:bg-red-900/20"
              onClick={handleCancel}
              disabled={isSaving}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-end gap-1">
            <span className={cn("font-semibold", valueClassName)}>
              {formatDisplayValue(value)}
            </span>
            {isAdmin && (
              <Button
                size="icon"
                variant="ghost"
                className="h-5 w-5 text-zinc-500 hover:text-blue-400 hover:bg-blue-900/20"
                onClick={() => setIsEditing(true)}
              >
                <Pencil className="h-3 w-3" />
              </Button>
            )}
          </div>
        )}
      </td>
    </tr>
  );
};

export default GameRulesEditableRow;
